import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Building2,
  TrendingUp,
  DollarSign,
  Loader2,
  Globe,
  Layers,
  ChevronDown,
  ChevronUp,
  BarChart3,
} from 'lucide-react';
import { api, indiaCompanyAPI, ukCompanyAPI } from '../../lib/api';
import { useWizardStore, type CompanyData, type CompanyCountry } from '../../stores/wizardStore';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card from '../../components/ui/Card';
import FocalCompanyFinancialPanel from '../../components/wizard/FocalCompanyFinancialPanel';
import FocalCompanyFinancials from '../../components/case-analytics/FocalCompanyFinancials';

// Maps the wizard's two-letter country codes onto the values
// FinancialDashboardTab expects ('US' | 'INDIA' | 'UK'). The wizard uses 'IN'
// for India while the financial backend uses 'INDIA' — converting here keeps
// the deep-financials lookup wired correctly per country.
function wizardCountryToFinancial(c: CompanyCountry | string | null | undefined): string | undefined {
  if (!c) return undefined;
  if (c === 'IN') return 'INDIA';
  return c;
}

// A unified, country-agnostic shape produced by `normalizeResults` for the
// search-result list and the "Selected company" details panel.
interface UnifiedCompany {
  country: CompanyCountry;
  // Stable id used for the React key and for the wizard's companyId.
  // US -> sec_<cik>, India -> ind_<scId>, UK -> uk_<companyNumber>
  companyId: string;
  name: string;
  ticker?: string;
  industry?: string;
  // Country-specific identifiers preserved for downstream lookups
  cik?: string;
  scId?: string;
  companyNumber?: string;
  // Optional UK-only signals from the availability probe
  availableYears?: number[];
  filingCount?: number;
}

interface Step1Props {
  onNext: () => void;
}

const COUNTRY_OPTIONS: { value: CompanyCountry; label: string; flag: string; description: string }[] = [
  { value: 'US', label: 'United States', flag: '🇺🇸', description: 'SEC EDGAR (10-K/10-Q)' },
  { value: 'IN', label: 'India', flag: '🇮🇳', description: 'Moneycontrol / NSE / BSE' },
  { value: 'UK', label: 'United Kingdom', flag: '🇬🇧', description: 'Companies House' },
];

export const Step1CompanySelection: React.FC<Step1Props> = ({ onNext }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { companyData, industryData, setCompanyData } = useWizardStore();

  const [country, setCountry] = useState<CompanyCountry>(companyData?.country || 'US');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<UnifiedCompany | null>(null);
  const [manualEntry, setManualEntry] = useState(false);
  // Collapsed by default — the deep financials panel renders the full P&L /
  // Balance Sheet / Sankey, which is much heavier than what most users want
  // to see while picking a company. Power users open it on demand.
  const [showDeepFinancials, setShowDeepFinancials] = useState(false);
  const [manualData, setManualData] = useState({
    name: companyData?.name || '',
    ticker: companyData?.ticker || '',
    industry: companyData?.industry || industryData?.classification || '',
    description: companyData?.description || '',
  });

  // Update form when companyData or industryData changes (e.g., when loading from saved case)
  useEffect(() => {
    if (companyData) {
      setManualData({
        name: companyData.name || '',
        ticker: companyData.ticker || '',
        industry: companyData.industry || industryData?.classification || '',
        description: companyData.description || '',
      });
      if (companyData.name) setManualEntry(true);
      if (companyData.country) setCountry(companyData.country);
    }
  }, [companyData, industryData]);

  // Reset selection when country changes so stale results don't carry over
  useEffect(() => {
    setSelectedCompany(null);
    setSearchQuery('');
  }, [country]);

  // Per-country search dispatcher. Returns the unified shape.
  const { data: searchResults, isLoading: isSearching } = useQuery<UnifiedCompany[]>({
    queryKey: ['company-search', country, searchQuery],
    queryFn: async () => {
      if (!searchQuery || searchQuery.length < 2) return [];

      if (country === 'US') {
        const raw = await api.get<Array<{ cik: string; name: string; ticker?: string; industry?: string }>>(
          '/companies/search',
          { query: searchQuery },
        );
        return (raw || []).map((c) => ({
          country: 'US' as CompanyCountry,
          companyId: `sec_${c.cik}`,
          cik: c.cik,
          name: c.name,
          ticker: c.ticker,
          industry: c.industry,
        }));
      }

      if (country === 'IN') {
        const res = await indiaCompanyAPI.searchCompanies(searchQuery);
        return (res.results || []).map((r: any) => ({
          country: 'IN' as CompanyCountry,
          companyId: `ind_${r.sc_id || r.stock_name?.toLowerCase().replace(/\s+/g, '_')}`,
          scId: r.sc_id,
          name: r.stock_name,
          ticker: r.sc_nse_symbol || r.sc_bse_symbol,
          // Yahoo classifies every Indian equity it returns. Prefer the finer
          // `industry` ("Information Technology Services") over `sector`
          // ("Technology") to match the granularity of the SEC SIC description
          // the US branch supplies.
          industry: r.industry || r.sector || undefined,
        }));
      }

      if (country === 'UK') {
        // UK has an availability probe rather than a multi-result search.
        // We model it as a single-result list so the UI flow is uniform.
        const r = await ukCompanyAPI.checkAvailability({ company_name: searchQuery });
        if (r.status !== 'success' || !r.company_name) return [];
        return [
          {
            country: 'UK' as CompanyCountry,
            companyId: `uk_${r.company_number}`,
            companyNumber: r.company_number || undefined,
            name: r.company_name,
            availableYears: r.available_years,
            filingCount: r.filing_count,
          },
        ];
      }

      return [];
    },
    enabled: searchQuery.length >= 2 && !manualEntry,
    staleTime: 30000,
  });

  const handleCompanySelect = async (company: UnifiedCompany) => {
    setSelectedCompany(company);

    // Resolve the industry, which Step 2 passes to /industry-research/smart as
    // `industryHint`. Without it that lookup has nothing to match on — the
    // company name is deliberately excluded from the candidate list — so the
    // library probe always misses and the user is pushed into a manual entry
    // or a full research run.
    //
    // Tier 1: whatever the registry search already returned. SEC supplies an
    // SIC description; Yahoo supplies sector/industry for Indian equities.
    let industry = company.industry;

    // Tier 2 (US only): SEC's basic-info carries the SIC description when the
    // ticker search row didn't.
    if (!industry && company.country === 'US' && company.cik) {
      try {
        const basicInfo = await api.get<{ industry?: string }>(`/companies/sec/${company.cik}/basic-info`);
        industry = basicInfo.industry;
      } catch (error) {
        console.error('Failed to fetch SEC basic info:', error);
      }
    }

    // Tier 3 (any country): fall back to the sector already stored on the
    // :Company node from a previous research run. Companies House returns no
    // classification at all, and Yahoo occasionally omits it, so this is the
    // only industry source for those cases.
    //
    // Uses the same queryKey FocalCompanyFinancialPanel is about to mount
    // with, so this populates the cache instead of adding a second request.
    if (!industry && company.companyId) {
      try {
        const snapshot = await queryClient.fetchQuery({
          queryKey: ['case-context', 'financial-snapshot', company.companyId, company.name],
          queryFn: () =>
            api.get<{ company?: { sector?: string | null } }>(
              `/case-discovery/companies/${encodeURIComponent(company.companyId)}/financial-snapshot`,
              { name: company.name },
            ),
          staleTime: 1000 * 60 * 5,
        });
        industry = snapshot?.company?.sector || undefined;
      } catch (error) {
        // Non-fatal — an unresearched company simply has no stored sector.
        console.warn('[company-select] sector lookup failed (non-fatal):', error);
      }
    }

    const companyDataForStore: CompanyData = {
      companyId: company.companyId,
      name: company.name,
      ticker: company.ticker,
      industry,
      country: company.country,
      cik: company.cik,
      scId: company.scId,
      companyNumber: company.companyNumber,
      description: `Company: ${company.name}${company.ticker ? ` (${company.ticker})` : ''}`,
    };

    setCompanyData(companyDataForStore);
  };

  const handleManualSubmit = () => {
    if (!manualData.name || !manualData.industry) return;
    const companyDataForStore: CompanyData = {
      name: manualData.name,
      ticker: manualData.ticker,
      industry: manualData.industry,
      description: manualData.description,
      country,
    };
    setCompanyData(companyDataForStore);
    setSelectedCompany({
      country,
      companyId: `manual_${Date.now()}`,
      name: manualData.name,
      ticker: manualData.ticker,
      industry: manualData.industry,
    });
  };

  const canProceed = () => {
    if (manualEntry) return !!manualData.name && !!manualData.industry;
    return selectedCompany !== null;
  };

  const handleNext = () => {
    if (manualEntry) handleManualSubmit();
    if (canProceed()) onNext();
  };

  const searchPlaceholder =
    country === 'US'
      ? 'Search by company name, ticker, or CIK…'
      : country === 'IN'
      ? 'Search by company name (NSE/BSE)…'
      : 'Enter UK company name…';

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-3xl font-bold text-neutral-900">Select Company for Analysis</h2>
        <p className="text-neutral-600">
          Search existing companies or enter company details manually
        </p>
      </div>

      {/* Country picker */}
      <Card className="p-4">
        <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 mb-3">
          <Globe className="w-4 h-4 text-neutral-500" />
          Country
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {COUNTRY_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setCountry(opt.value)}
              className={`flex items-center gap-3 p-3 border-2 rounded-lg text-left transition-all ${
                country === opt.value
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-neutral-200 hover:border-neutral-300 bg-white'
              }`}
            >
              <span className="text-2xl">{opt.flag}</span>
              <div className="flex-1">
                <p className="font-semibold text-neutral-900 text-sm">{opt.label}</p>
                <p className="text-xs text-neutral-500">{opt.description}</p>
              </div>
              {country === opt.value && (
                <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      </Card>

      {/* Search or Manual Toggle */}
      <div className="flex justify-center gap-4 p-4 bg-neutral-50 rounded-lg">
        <Button
          variant={!manualEntry ? 'primary' : 'outline'}
          onClick={() => setManualEntry(false)}
          className="flex items-center gap-2"
        >
          <Search className="w-4 h-4" />
          Search Companies
        </Button>
        <Button
          variant={manualEntry ? 'primary' : 'outline'}
          onClick={() => setManualEntry(true)}
          className="flex items-center gap-2"
        >
          <Building2 className="w-4 h-4" />
          Enter Manually
        </Button>
      </div>

      {/* Company Search Section */}
      {!manualEntry && (
        <Card className="p-6">
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 w-5 h-5 text-neutral-400" />
              <Input
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-3 text-lg"
              />
              {isSearching && (
                <Loader2 className="absolute right-3 top-3 w-5 h-5 text-primary-500 animate-spin" />
              )}
            </div>

            {searchResults && searchResults.length > 0 && (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {searchResults.map((company) => (
                  <Card
                    key={company.companyId}
                    className={`p-4 cursor-pointer transition-all hover:shadow-md ${
                      selectedCompany?.companyId === company.companyId
                        ? 'border-2 border-primary-500 bg-primary-50'
                        : 'border border-neutral-200'
                    }`}
                    onClick={() => handleCompanySelect(company)}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-lg text-neutral-900">
                          {company.name}
                          {company.ticker && (
                            <span className="ml-2 text-primary-600">({company.ticker})</span>
                          )}
                        </h3>
                        {company.industry && (
                          <p className="text-sm text-neutral-600 mt-1">{company.industry}</p>
                        )}
                        {company.country === 'UK' && company.filingCount != null && (
                          <p className="text-sm text-neutral-500 mt-1">
                            {company.filingCount} filings available
                            {company.availableYears && company.availableYears.length > 0 &&
                              ` (${Math.min(...company.availableYears)}–${Math.max(...company.availableYears)})`}
                          </p>
                        )}
                      </div>
                      {selectedCompany?.companyId === company.companyId && (
                        <div className="flex items-center gap-2 text-primary-600 font-medium">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Selected
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {searchQuery.length >= 2 && searchResults?.length === 0 && !isSearching && (
              <div className="text-center py-8 text-neutral-500">
                <Building2 className="w-12 h-12 mx-auto mb-3 text-neutral-300" />
                <p>No companies found matching "{searchQuery}"</p>
                <p className="text-sm mt-2">Try entering company details manually</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Manual Entry Form */}
      {manualEntry && (
        <Card className="p-6">
          <h3 className="text-xl font-semibold text-neutral-900 mb-4">
            Enter Company Details Manually
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Company Name <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                placeholder="e.g., Acme Corporation"
                value={manualData.name}
                onChange={(e) => setManualData({ ...manualData, name: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Ticker Symbol (Optional)
              </label>
              <Input
                type="text"
                placeholder="e.g., ACME"
                value={manualData.ticker}
                onChange={(e) => setManualData({ ...manualData, ticker: e.target.value.toUpperCase() })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Industry <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                placeholder="e.g., Technology / Software"
                value={manualData.industry}
                onChange={(e) => setManualData({ ...manualData, industry: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Description (Optional)
              </label>
              <textarea
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows={3}
                placeholder="Brief description of the company..."
                value={manualData.description}
                onChange={(e) => setManualData({ ...manualData, description: e.target.value })}
              />
            </div>
          </div>
        </Card>
      )}

      {/* Focal-company financial snapshot (read-only enrichment).
          Renders below selection/manual-entry. Backend resolves synthetic
          companyKeys via the companyName fallback so that pre-existing
          Company Research (under a different real companyId) is still found.
          When resolved, we auto-fill the manual-entry Industry / Ticker
          fields so the user doesn't have to re-type data we already have
          — keeping the existing manualData state shape so the rest of
          Step 1's logic is unchanged. */}
      {(selectedCompany?.companyId || companyData?.companyId || selectedCompany?.name || companyData?.name) && (
        <FocalCompanyFinancialPanel
          companyKey={selectedCompany?.companyId || companyData?.companyId || (selectedCompany?.name ?? companyData?.name ?? '')}
          companyId={selectedCompany?.companyId || companyData?.companyId}
          companyName={selectedCompany?.name || companyData?.name}
          country={selectedCompany?.country || companyData?.country || country}
          onCompanyResolved={(resolved) => {
            // Auto-fill only the Industry field when we have an existing
            // Company Research record AND the user is in manual-entry mode
            // AND the field is currently empty. We never overwrite anything
            // the user has typed.
            if (manualEntry && !manualData.industry && resolved.sector) {
              setManualData((prev) =>
                prev.industry ? prev : { ...prev, industry: resolved.sector || '' },
              );
            }
            // For a SELECTED (searched) company, back-fill its Industry from the
            // country-scoped Company Research sector — India/UK search adapters
            // return no industry, so this is where a selected company gets one.
            // Never overwrite an industry the search/user already provided.
            if (!manualEntry && companyData && !companyData.industry && resolved.sector) {
              setCompanyData({ ...companyData, industry: resolved.sector });
            }
            if (manualEntry && !manualData.ticker && resolved.ticker) {
              setManualData((prev) =>
                prev.ticker ? prev : { ...prev, ticker: resolved.ticker || '' },
              );
            }
          }}
        />
      )}

      {/* Business Structure card — surfaces whatever revenue segments are
          already on the wizard store (typically present when re-entering the
          wizard from a saved case or when a previous step populated them).
          Business units / regions need backend-side `businessStructure` exposed
          pre-case to render — they show a hint pointing to Company Research
          when missing. The card itself only mounts when we have *some* segment
          data; otherwise it stays out of the way. */}
      {companyData?.financialData?.segments && companyData.financialData.segments.length > 0 && (
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Layers className="w-4 h-4 text-primary-600" />
            <h4 className="text-sm font-semibold text-neutral-900">Business Structure</h4>
            <span className="text-xs text-neutral-500">
              {companyData.financialData.segments.length} segment
              {companyData.financialData.segments.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {companyData.financialData.segments.map((segment, idx) => (
              <div
                key={`${segment.name}-${idx}`}
                className="px-3 py-1 bg-neutral-50 border border-neutral-200 rounded-full text-sm text-neutral-700"
              >
                {segment.name}
                {segment.percentage != null && (
                  <span className="ml-1.5 text-xs text-neutral-500">({segment.percentage}%)</span>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-neutral-400 mt-3">
            Business units &amp; geographic regions populate after the company is processed through Company Research.
          </p>
        </Card>
      )}

      {/* Deep-financials expand toggle. The compact FocalCompanyFinancialPanel
          above renders a 4-tile snapshot for at-a-glance use; this toggle
          opens the same FinancialDashboardTab section that CaseDetail uses
          (P&L + Balance Sheet + Revenue→EV Sankey) inline so users can drill
          in without leaving the wizard. Mounts only when a company name is
          present — the component itself short-circuits to a no-data state if
          the company hasn't been processed through Company Research yet. */}
      {(selectedCompany?.name || companyData?.name) && (
        <div>
          <button
            type="button"
            onClick={() => setShowDeepFinancials((v) => !v)}
            className="flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
            aria-expanded={showDeepFinancials}
          >
            <BarChart3 className="w-4 h-4" />
            {showDeepFinancials ? 'Hide deep financials' : 'View deep financials'}
            {showDeepFinancials ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
          {showDeepFinancials && (
            <div className="mt-3">
              <FocalCompanyFinancials
                companyName={selectedCompany?.name || companyData?.name}
                country={wizardCountryToFinancial(
                  selectedCompany?.country || companyData?.country || country,
                )}
                mode="full"
              />
            </div>
          )}
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6 border-t border-neutral-200">
        <Button variant="outline" onClick={() => navigate('/case-library')}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleNext}
          disabled={!canProceed()}
          className="flex items-center gap-2"
        >
          Next: Industry Research
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Button>
      </div>
    </div>
  );
};

// Note: the rich "Selected company details" panel that the previous version of
// this file rendered for US-only SEC results has been removed in this multi-
// country refactor — it depended on US-specific `latestReport` / `segments`
// fields that India and UK searches don't return. We can reintroduce a
// country-aware details panel later if needed.

export default Step1CompanySelection;
